import { Spinner } from '@frontend/components/common';
import { ENTITY_ICONS, Itsme } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage, useIntl } from 'react-intl';

import SignInCta from '../../../home/sign-in-cta/sign-in-cta.component';
import { ItsmeQrAuthenticationContent } from '../../components/itsme-qr-authentication-content/itsme-qr-authentication-content.component';
import { MethodButton } from '../../components/method-button/method-button';
import { MethodOrDivider } from '../../components/method-or-divider.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useProveIdentity, { ProveIdentityMode } from './prove-identity.controller';

const ID = 'prove-identity';

interface ProveIdentityProps {
    mode?: ProveIdentityMode;
}

const ProveIdentity = ({ mode = 'both' }: ProveIdentityProps) => {
    const intl = useIntl();
    const viewProps = useProveIdentity(mode);
    const showDenied = viewProps.phase === 'denied' || viewProps.accessDenied;
    const showSpinner = !showDenied && (viewProps.phase === 'loading' || viewProps.isProcessing || viewProps.isIdentifying);
    // Access-denied for an already-logged-in user is shown in-page; keep the modal for other errors.
    const showErrorModal = !!viewProps.error && !(showDenied && viewProps.alreadyAuthenticated);

    return (
        <WizardScreenContainer
            id={ID}
            loadingModal={{
                show: showSpinner
            }}
            errorModal={{
                show: showErrorModal,
                title: CommonMessage.ERRORS.DEFAULT,
                message: viewProps.error,
                onClose: viewProps.resetError
            }}>
            {showDenied ? (
                <div className='flex size-full flex-col items-center justify-center gap-3 p-6 text-center'>
                    <p className='text-foreground-default text-base font-medium'>{viewProps.accessDeniedMessage}</p>
                    {viewProps.alreadyAuthenticated && (
                        <p className='text-brand-muted text-sm font-medium'>
                            <FormattedMessage
                                id='kiosk.wizard.prove-identity.access-denied-go-back'
                                description='Hint shown when a logged-in user is denied on the prove identity screen'
                                defaultMessage='Go back to choose another method or transaction.'
                            />
                        </p>
                    )}
                </div>
            ) : viewProps.phase === 'loading' ? (
                <div className='flex size-full items-center justify-center'>
                    <Spinner className='text-border-default fill-brand-primary size-12' />
                </div>
            ) : viewProps.showItsmeAuth ? (
                <div className='flex size-full flex-col gap-3'>
                    <div className={`flex flex-1 flex-col items-center ${mode === 'itsme' ? 'justify-start' : 'justify-center'}`}>
                        <ItsmeQrAuthenticationContent {...viewProps.itsmeViewProps} />
                    </div>
                    {viewProps.showAuthMethodToggle && (
                        <>
                            <MethodOrDivider />
                            <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                                <MethodButton
                                    label={intl.formatMessage({
                                        id: 'kiosk.wizard.prove-identity.use-badge',
                                        description: 'Button label to switch from itsme authentication to badge authentication on the prove identity screen',
                                        defaultMessage: 'Use badge instead'
                                    })}
                                    onClick={viewProps.onSelectBadgeAuth}
                                    icon={<ENTITY_ICONS.BADGE />}
                                />
                            </div>
                        </>
                    )}
                </div>
            ) : viewProps.showBadgeAuth ? (
                <div className='flex size-full flex-col gap-3'>
                    <div className='flex flex-1 flex-col items-center justify-center'>
                        <SignInCta
                            theme='light'
                            title={
                                <FormattedMessage
                                    id='kiosk.wizard.prove-identity.scan-badge-title'
                                    description='Title shown on the prove identity screen when the user must identify themselves with a badge'
                                    defaultMessage='Scan your badge to continue'
                                />
                            }
                        />
                    </div>
                    {viewProps.showAuthMethodToggle && (
                        <>
                            <MethodOrDivider />
                            <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                                <MethodButton
                                    label={intl.formatMessage({
                                        id: 'kiosk.wizard.method-select.login-itsme',
                                        description: 'Label for button to login with itsme',
                                        defaultMessage: 'Login with itsme'
                                    })}
                                    icon={<Itsme className='text-always-black size-5' />}
                                    onClick={viewProps.onSelectItsmeAuth}
                                />
                            </div>
                        </>
                    )}
                </div>
            ) : null}
        </WizardScreenContainer>
    );
};

export default ProveIdentity;
